import React, { useEffect, useMemo, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { Box, Button, Typography } from '@material-ui/core';
import HttpService from '../services/httpService';
import { setSnackInfo } from '../redux/actions/appAction';
import { ROLENAME } from '../utils';

const CITY_HEADER_BG = '#1e3a8a';
const CITY_HEADER_FG = '#ffffff';
const EC_HEADER_BG = '#6b7280';
const EC_HEADER_FG = '#ffffff';
const MGR_ROW_BG = '#ffedd5';
const MGR_ROW_FG = '#000000';
const ASM_HEADER_BG = '#e5e7eb';
const TABLE_TEXT_COLOR = '#000000';
const CELL_BORDER_COLOR = '#000000';
const CELL_BORDER = `0.5px solid ${CELL_BORDER_COLOR}`;
const DEFAULT_CELL_BG = '#ffffff';

const CELL_TEXT_WRAP = {
  maxWidth: '100%',
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
};

function gridCellBorders(colIndex, rowIndex, colCount, rowCount) {
  return {
    boxSizing: 'border-box',
    borderColor: CELL_BORDER_COLOR,
    borderRight: colIndex < colCount - 1 ? CELL_BORDER : undefined,
    borderBottom: rowIndex < rowCount - 1 ? CELL_BORDER : undefined,
    minWidth: 0,
  };
}

function hexToRgba(hex, alpha = 0.32) {
  const h = String(hex || '').trim();
  if (!/^#[0-9a-fA-F]{6}$/.test(h)) return undefined;
  const n = parseInt(h.slice(1), 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function resolveRowStatus(row) {
  if (!row || typeof row !== 'object') return '';
  const nested = row.hrStatusColorCodeId || row.hrStatusColorCode;
  if (nested && typeof nested === 'object') {
    const fromCode = String(nested.status || '').trim();
    if (fromCode) return fromCode;
  }
  return String(row.status || row.rosterStatus || '').trim();
}

function resolveStatusKey(row) {
  if (!row) return '';
  const id =
    row.hrStatusColorCodeId?._id ||
    row.hrStatusColorCodeId ||
    row.hrStatusColorCode?._id ||
    row.hrStatusColorCode;
  if (id) return String(id);
  return resolveRowStatus(row);
}

function formatBracketStatusLabel(status) {
  const s = String(status || '').toLowerCase().replace(/\s+/g, ' ').trim();
  if (!s) return '';
  if (s === 'active') return 'Active';
  if (s === 'ok') return 'Ok';
  if (s.includes('open req')) return 'Open Req';
  if (s.includes('yet to join')) return 'Yet to Join';
  if (s.includes('background verification failed')) return 'Background Verification Failed';
  if (s.includes('buffer') && s.includes('idd')) return 'Buffer (IDD)';
  return String(status).trim();
}

function shouldShowStatusInBracket(status) {
  return Boolean(String(status || '').trim());
}

function isLastGroupMemberStatus(row) {
  if (!row || typeof row !== 'object') return false;
  const st = resolveRowStatus(row).toLowerCase().replace(/\s+/g, ' ').trim();
  const note = String(row.statusNote || '').toLowerCase().replace(/\s+/g, ' ').trim();
  const check = (s) =>
    s.includes('open req') ||
    s.includes('yet to join') ||
    (s.includes('buffer') && s.includes('idd')) ||
    s.includes('buffer(idd)');
  return check(st) || check(note);
}

function sortMembersForDisplay(members) {
  const list = Array.isArray(members) ? members : [];
  const primary = [];
  const lastGroup = [];
  for (const member of list) {
    if (isLastGroupMemberStatus(member)) lastGroup.push(member);
    else primary.push(member);
  }
  return [...primary, ...lastGroup];
}

const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function ordinalDay(n) {
  const j = n % 10;
  const k = n % 100;
  if (j === 1 && k !== 11) return `${n}st`;
  if (j === 2 && k !== 12) return `${n}nd`;
  if (j === 3 && k !== 13) return `${n}rd`;
  return `${n}th`;
}

function isInvalidJoiningYear(y) {
  return !y || y <= 1970 || y < 1980;
}

function formatJoiningFromYmd(y, m, day) {
  if (!m || !day || isInvalidJoiningYear(y)) return '';
  return `${ordinalDay(day)} ${MONTHS_SHORT[m - 1]} ${y}`;
}

function parseJoiningDateParts(value) {
  if (value == null || value === '') return null;
  const s = String(value).trim();
  if (!s) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, day] = s.split('-').map(Number);
    if (isInvalidJoiningYear(y)) return null;
    return { y, m, day };
  }
  const parsed =
    value instanceof Date
      ? value
      : typeof value === 'number' && Number.isFinite(value)
        ? new Date(value)
        : new Date(s);
  if (Number.isNaN(parsed.getTime())) return null;
  const y = parsed.getFullYear();
  if (isInvalidJoiningYear(y)) return null;
  return { y, m: parsed.getMonth() + 1, day: parsed.getDate() };
}

function formatJoining(value) {
  const parts = parseJoiningDateParts(value);
  if (!parts) return '';
  return formatJoiningFromYmd(parts.y, parts.m, parts.day);
}

function resolveStatusDateForRow(row) {
  if (!row || typeof row !== 'object') return null;
  const s = resolveRowStatus(row).toLowerCase().replace(/\s+/g, ' ').trim();
  if (s.includes('open req')) return row.openReqDate ?? null;
  if (s === 'relieved') return row.relievedDate ?? null;
  if (s === 'transferred') return row.transferredDate ?? null;
  if (s === 'terminated') return row.terminatedDate ?? null;
  if (s === 'maternity') return row.maternityDate ?? null;
  if (s === 'industry') return row.industryDate ?? null;
  if (s === 'abscond') return row.abscondDate ?? null;
  if (s === 'resigned') return row.resignedDate ?? null;
  if (s === 'warning') return row.warningDate ?? null;
  if ((s.includes('buffer') && s.includes('idd')) || s.includes('buffer(idd)')) {
    return row.bufferIddDate ?? null;
  }
  if (s === 'active - training' || (s.includes('active') && s.includes('training'))) {
    return row.activeTrainingDate ?? null;
  }
  if (s === 'pip') return row.pipDate ?? null;
  return null;
}

function resolveRawJoiningDate(row) {
  if (!row || typeof row !== 'object') return null;
  return (
    row.joiningDate ??
    row.joining_date ??
    row.dateOfJoining ??
    row.date_of_joining ??
    row.doj ??
    row.joinDate ??
    row.joinedOn ??
    row.user?.joiningDate ??
    row.employee?.joiningDate ??
    row.rosterUser?.joiningDate ??
    null
  );
}

function isJoiningDateOnlyStatus(normalized) {
  return (
    normalized === 'yet to join' ||
    normalized === 'active' ||
    (normalized.includes('active') && normalized.includes('training'))
  );
}

function getPersonDateLines(person) {
  if (!person || typeof person !== 'object') {
    return { statusDate: '', joiningDate: '' };
  }
  const normalized = resolveRowStatus(person).toLowerCase().replace(/\s+/g, ' ').trim();
  if (isJoiningDateOnlyStatus(normalized)) {
    return { statusDate: formatJoining(resolveRawJoiningDate(person)), joiningDate: '' };
  }
  return { statusDate: formatJoining(resolveStatusDateForRow(person)), joiningDate: '' };
}

function statusSuffixBracket(row) {
  if (!row || typeof row !== 'object') return '';
  const st = resolveRowStatus(row);
  const note = String(row.statusNote || '').trim();
  const risk = String(row.atRiskCategory || '').trim();
  if (shouldShowStatusInBracket(st)) return ` (${formatBracketStatusLabel(st)})`;
  if (!st && shouldShowStatusInBracket(note)) return ` (${formatBracketStatusLabel(note)})`;
  if (shouldShowStatusInBracket(risk)) return ` (${formatBracketStatusLabel(risk)})`;
  if (shouldShowStatusInBracket(note)) return ` (${formatBracketStatusLabel(note)})`;
  return '';
}

function hasPersonName(person) {
  if (!person || typeof person !== 'object') return false;
  const normalized = resolveRowStatus(person).toLowerCase().replace(/\s+/g, ' ').trim();
  if (normalized.includes('open req')) return true;
  return Boolean(String(person?.name || person?.employeeName || '').trim());
}

function getPersonDisplayName(person) {
  if (!person || typeof person !== 'object') return '—';
  const name = String(person.name || person.employeeName || '').trim();
  if (name) return name;
  const normalized = resolveRowStatus(person).toLowerCase().replace(/\s+/g, ' ').trim();
  if (normalized.includes('open req')) return 'Open Req';
  return '—';
}

function isOpenReqStatusPerson(person) {
  if (!person || typeof person !== 'object') return false;
  return resolveRowStatus(person).toLowerCase().replace(/\s+/g, ' ').trim().includes('open req');
}

function getPersonNameLine(person) {
  const displayName = getPersonDisplayName(person);
  const hasRealName = Boolean(String(person?.name || person?.employeeName || '').trim());
  if (isOpenReqStatusPerson(person) && !hasRealName) {
    return displayName;
  }
  return `${displayName}${statusSuffixBracket(person)}`;
}

function PersonCellContent({ person, prefix, align = 'center', bold = false }) {
  if (!person && !prefix) return null;
  if (typeof person === 'string') {
    const label = prefix ? `${prefix}: ${person}` : person;
    return (
      <Typography
        component="span"
        style={{
          fontSize: 14,
          color: TABLE_TEXT_COLOR,
          fontWeight: bold || prefix ? 700 : 400,
          lineHeight: 1.35,
          textAlign: align,
          display: 'block',
          ...CELL_TEXT_WRAP,
        }}
      >
        {label}
      </Typography>
    );
  }
  if (!person) {
    return (
      <Typography
        component="span"
        style={{
          fontSize: 14,
          color: TABLE_TEXT_COLOR,
          fontWeight: bold || prefix ? 700 : 400,
          textAlign: align,
          display: 'block',
        }}
      >
        {prefix ? `${prefix}: —` : '—'}
      </Typography>
    );
  }

  const nameLine = getPersonNameLine(person);
  const { statusDate, joiningDate } = getPersonDateLines(person);
  const alignItems = align === 'left' ? 'flex-start' : 'center';

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems={alignItems}
      style={{ gap: 2, width: '100%', boxSizing: 'border-box' }}
    >
      <Typography
        component="span"
        style={{
          fontSize: 14,
          color: TABLE_TEXT_COLOR,
          fontWeight: bold || prefix ? 700 : 400,
          lineHeight: 1.35,
          textAlign: align,
          display: 'block',
          ...CELL_TEXT_WRAP,
        }}
      >
        {prefix ? `${prefix}: ${nameLine}` : nameLine}
      </Typography>
      {statusDate ? (
        <Typography
          component="span"
          style={{
            fontSize: 12,
            color: TABLE_TEXT_COLOR,
            lineHeight: 1.25,
            textAlign: align,
            display: 'block',
            ...CELL_TEXT_WRAP,
          }}
        >
          {statusDate}
        </Typography>
      ) : null}
      {joiningDate ? (
        <Typography
          component="span"
          style={{
            fontSize: 12,
            color: TABLE_TEXT_COLOR,
            lineHeight: 1.25,
            textAlign: align,
            display: 'block',
            ...CELL_TEXT_WRAP,
          }}
        >
          {joiningDate}
        </Typography>
      ) : null}
    </Box>
  );
}

function buildSalesHierarchyView(apiData) {
  if (!apiData || typeof apiData !== 'object') return { cities: [] };
  if (!Array.isArray(apiData.cities)) return { cities: [] };

  const resolveAssistantSalesManagersForLocation = (loc) => {
    const asms = Array.isArray(loc.assistantSalesManagers) ? loc.assistantSalesManagers : [];
    if (asms.length > 0) {
      return asms.map((asm) => ({
        ...asm,
        salesUsers: sortMembersForDisplay(
          Array.isArray(asm.salesUsers) ? asm.salesUsers.filter(hasPersonName) : []
        ),
      }));
    }

    const directSalesUsers = sortMembersForDisplay(
      Array.isArray(loc.salesUsers) ? loc.salesUsers.filter(hasPersonName) : []
    );
    if (directSalesUsers.length === 0) return [];

    return [
      {
        _id: `direct-sales-users-${loc.ec || loc.experienceCenter || 'loc'}`,
        salesUsers: directSalesUsers,
        isDirectSalesUsersGroup: true,
      },
    ];
  };

  return {
    cities: apiData.cities
      .map((cityBlock) => ({
        city: cityBlock.city || cityBlock.cityName || '—',
        locations: (cityBlock.locations || [])
          .map((loc) => ({
            ec: loc.ec || loc.experienceCenter || '—',
            department: loc.department || '',
            dgm: hasPersonName(loc.dgm) ? loc.dgm : null,
            salesManagers: Array.isArray(loc.salesManagers)
              ? loc.salesManagers.filter(hasPersonName)
              : [],
            assistantSalesManagers: resolveAssistantSalesManagersForLocation(loc),
          }))
          .filter(
            (loc) => loc.salesManagers.length > 0 || loc.assistantSalesManagers.length > 0
          ),
      }))
      .filter((cityBlock) => cityBlock.locations.length > 0),
  };
}

function statusBackgroundOr(row, getStatusCellBackground, fallbackBg) {
  if (!row || typeof row !== 'object') return fallbackBg;
  const bg = getStatusCellBackground(row);
  return bg && bg !== DEFAULT_CELL_BG ? bg : fallbackBg;
}

function HierarchyRow({ children, bg = MGR_ROW_BG }) {
  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      py={1.5}
      px={2}
      style={{
        backgroundColor: bg,
        color: MGR_ROW_FG,
        border: CELL_BORDER,
        borderTop: 'none',
        borderColor: CELL_BORDER_COLOR,
        textAlign: 'center',
        width: '100%',
        boxSizing: 'border-box',
        flexShrink: 0,
        minHeight: 'fit-content',
        overflow: 'visible',
      }}
    >
      {children}
    </Box>
  );
}

function AsmColumnGrid({ asms, getStatusCellBackground, locationKey }) {
  const columns = asms.length > 0 ? asms : [];
  const colCount = Math.max(columns.length, 1);
  const maxRows = columns.length
    ? Math.max(...columns.map((a) => (a.salesUsers || []).length), 0)
    : 0;
  const showAsmHeaderRow = columns.some((asm) => !asm.isDirectSalesUsersGroup);

  if (columns.length === 0) {
    return (
      <Box
        py={2}
        px={2}
        style={{
          border: CELL_BORDER,
          borderTop: 'none',
          borderColor: CELL_BORDER_COLOR,
          textAlign: 'center',
          color: TABLE_TEXT_COLOR,
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        No assistant sales managers
      </Box>
    );
  }

  const headerRowCount = showAsmHeaderRow ? 1 : 0;
  const bodyRowCount = maxRows === 0 ? 1 : maxRows;
  const totalRows = headerRowCount + bodyRowCount;

  return (
    <Box
      style={{
        width: '100%',
        flexShrink: 0,
        boxSizing: 'border-box',
        border: CELL_BORDER,
        borderTop: 'none',
        borderColor: CELL_BORDER_COLOR,
        display: 'grid',
        gridTemplateColumns: `repeat(${colCount}, minmax(0, 1fr))`,
      }}
    >
      {showAsmHeaderRow
        ? columns.map((asm, ci) => (
            <Box
              key={asm._id || `asm-h-${locationKey}-${ci}`}
              py={1}
              px={0.5}
              style={{
                ...gridCellBorders(ci, 0, colCount, totalRows),
                backgroundColor: asm.isDirectSalesUsersGroup
                  ? ASM_HEADER_BG
                  : statusBackgroundOr(asm, getStatusCellBackground, ASM_HEADER_BG),
                minHeight: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {asm.isDirectSalesUsersGroup ? (
                <Typography style={{ fontSize: 14, color: TABLE_TEXT_COLOR, ...CELL_TEXT_WRAP }}>
                  —
                </Typography>
              ) : (
                <PersonCellContent person={asm} bold />
              )}
            </Box>
          ))
        : null}

      {Array.from({ length: bodyRowCount }).flatMap((_, bodyRi) => {
        const gridRowIndex = headerRowCount + bodyRi;
        return columns.map((asm, ci) => {
          const user = maxRows === 0 ? null : (asm.salesUsers || [])[bodyRi];
          return (
            <Box
              key={user?._id || `u-${locationKey}-${ci}-${bodyRi}`}
              py={1.2}
              px={0.5}
              style={{
                ...gridCellBorders(ci, gridRowIndex, colCount, totalRows),
                backgroundColor: user ? getStatusCellBackground(user) : '#fafafa',
                minHeight: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {user ? (
                <PersonCellContent person={user} align="center" />
              ) : (
                <Typography style={{ color: TABLE_TEXT_COLOR, ...CELL_TEXT_WRAP }}>{'\u00a0'}</Typography>
              )}
            </Box>
          );
        });
      })}
    </Box>
  );
}

function LocationBlock({ loc, city, getStatusCellBackground }) {
  const locKey = `${city}-${loc.ec}`;

  return (
    <Box
      display="flex"
      flexDirection="column"
      width="100%"
      style={{ boxSizing: 'border-box', flexShrink: 0 }}
    >
      <Box
        py={1}
        px={2}
        style={{
          backgroundColor: EC_HEADER_BG,
          color: EC_HEADER_FG,
          fontWeight: 700,
          textAlign: 'center',
          border: CELL_BORDER,
          borderTop: 'none',
          borderColor: CELL_BORDER_COLOR,
          width: '100%',
          boxSizing: 'border-box',
          flexShrink: 0,
        }}
      >
        {loc.ec}
      </Box>

      {/* DGM row hidden per product request
      <HierarchyRow>
        <PersonCellContent person={loc.dgm} prefix="DGM" />
      </HierarchyRow>
      */}

      {loc.salesManagers.length > 0 ? (
        loc.salesManagers.map((sm) => (
          <HierarchyRow
            key={sm._id || `${locKey}-sm-${sm.emailId}`}
            bg={statusBackgroundOr(sm, getStatusCellBackground, MGR_ROW_BG)}
          >
            <PersonCellContent person={sm} prefix="Sales Manager" />
          </HierarchyRow>
        ))
      ) : (
        <HierarchyRow>
          <Typography style={{ fontWeight: 700, color: MGR_ROW_FG, fontSize: 14 }}>
            Sales Manager: —
          </Typography>
        </HierarchyRow>
      )}

      <AsmColumnGrid
        asms={loc.assistantSalesManagers}
        getStatusCellBackground={getStatusCellBackground}
        locationKey={locKey}
      />
    </Box>
  );
}

function HrRacksheetSalesHierarchy(props) {
  const { userDetails } = useSelector((state) => state.user);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hrStatusColorCodes, setHrStatusColorCodes] = useState([]);

  const loadHrStatusColorCodes = async () => {
    try {
      const response = await HttpService.getHrStatusColorCodes();
      setHrStatusColorCodes(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      console.error('Error loading HR status color codes:', error);
      setHrStatusColorCodes([]);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const response = await HttpService.getRosterSalesHierarchy();
      if (response?.data?.success === false) {
        props.setSnackInfo(
          response?.data?.message || 'Failed to load sales hierarchy',
          'error'
        );
        setPayload(null);
        return;
      }
      setPayload(response?.data?.data ?? response?.data ?? null);
    } catch (error) {
      console.error('Error loading sales hierarchy:', error);
      props.setSnackInfo(
        error?.response?.data?.message || 'Error loading sales hierarchy',
        'error'
      );
      setPayload(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadHrStatusColorCodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getStatusCellBackground = useMemo(() => {
    return (row) => {
      const key = resolveStatusKey(row);
      if (!key) return DEFAULT_CELL_BG;
      const normalizedKey = key.toLowerCase();
      const hit = hrStatusColorCodes.find(
        (item) =>
          String(item?._id) === key ||
          String(item?.status || '').trim().toLowerCase() === normalizedKey
      );
      if (!hit?.hexCode) return DEFAULT_CELL_BG;
      return hexToRgba(hit.hexCode, 0.32) || hit.hexCode;
    };
  }, [hrStatusColorCodes]);

  const view = useMemo(() => {
    const built = buildSalesHierarchyView(payload);
    const hasFullAccess = userDetails?.roles?.some(
      (role) =>
        role?.name === ROLENAME.Admin ||
        role?.name === ROLENAME.HRUser ||
        role?.name === ROLENAME.HRManager || 
        role?.name === ROLENAME.HRAssistantManager
    );
    if (hasFullAccess) return built;
    const userCityNames = (userDetails?.locationId || [])
      .map((loc) => loc?.name)
      .filter(Boolean);
    return { cities: built.cities.filter((cityBlock) => userCityNames.includes(cityBlock.city)) };
  }, [payload, userDetails]);
  const title = (payload && payload.title) || 'Sales hierarchy — EC view';

  return (
    <Box p={2}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        style={{ gap: 12 }}
        mb={2}
      >
        <Typography variant="h5" style={{ fontWeight: 700, color: CITY_HEADER_BG }}>
          {title}
        </Typography>
        <Button
          variant="outlined"
          disabled={loading}
          onClick={() => {
            load();
            loadHrStatusColorCodes();
          }}
          style={{ borderColor: '#F47E46', color: '#F47E46' }}
        >
          {loading ? 'Loading…' : 'Refresh'}
        </Button>
      </Box>

      {!loading && view.cities.length === 0 && (
        <Typography variant="body2" color="textSecondary">
          No sales hierarchy data available.
        </Typography>
      )}

      {!loading && view.cities.length > 0 && (
        <Box style={{ width: '100%', overflowX: 'auto' }}>
          <Box
            display="flex"
            flexWrap="nowrap"
            alignItems="flex-start"
            style={{ gap: 16, minWidth: 'min-content' }}
          >
            {view.cities.map((cityBlock, cityIdx) => (
              <Box
                key={`city-col-${cityIdx}-${cityBlock.city}`}
                display="flex"
                flexDirection="column"
                style={{
                  flex: '0 0 auto',
                  minWidth: 300,
                  width: 420,
                  boxSizing: 'border-box',
                }}
              >
                <Box
                  py={1.2}
                  px={2}
                  style={{
                    backgroundColor: CITY_HEADER_BG,
                    color: CITY_HEADER_FG,
                    fontWeight: 700,
                    textAlign: 'center',
                    border: CELL_BORDER,
                    borderColor: CELL_BORDER_COLOR,
                  }}
                >
                  {cityBlock.city}
                </Box>

                {(cityBlock.locations || []).map((loc) => (
                  <LocationBlock
                    key={`${cityBlock.city}-${loc.ec}`}
                    loc={loc}
                    city={cityBlock.city}
                    getStatusCellBackground={getStatusCellBackground}
                  />
                ))}
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default connect(null, { setSnackInfo })(HrRacksheetSalesHierarchy);
